import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { MessageInbox } from "./MessageInbox";
import { MessageComposer } from "./MessageComposer";
import { MessageThread } from "./MessageThread";
import { Mail, Send, Users } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type Message = Database["public"]["Tables"]["messages"]["Row"];

export const MessageCenter = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setCurrentUserId(user.id);

      const { data, error } = await supabase
        .from("messages")
        .select("*")
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setMessages(data || []);
      setUnreadCount(data?.filter(msg => 
        msg.recipient_id === user.id && msg.status === 'unread'
      ).length || 0);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageSent = () => {
    fetchMessages();
  };

  const handleMessageRead = (message: Message) => {
    setSelectedMessage(message);
    if (message.status === 'unread') {
      markAsRead(message.id);
    }
  };

  const markAsRead = async (messageId: string) => {
    try {
      const { error } = await supabase
        .from("messages")
        .update({ 
          status: 'read',
          read_at: new Date().toISOString()
        })
        .eq("id", messageId);

      if (error) throw error;
      
      fetchMessages();
    } catch (error) {
      console.error("Error marking message as read:", error);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">Loading messages...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Message Center
            {unreadCount > 0 && (
              <Badge variant="destructive">{unreadCount}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="inbox" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="inbox" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Inbox
              </TabsTrigger>
              <TabsTrigger value="compose" className="flex items-center gap-2">
                <Send className="h-4 w-4" />
                Compose
              </TabsTrigger>
              <TabsTrigger value="sent" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Sent
              </TabsTrigger>
            </TabsList>

            <TabsContent value="inbox" className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <MessageInbox
                    messages={messages.filter(msg => 
                      msg.recipient_id === currentUserId
                    )}
                    onMessageSelect={handleMessageRead}
                    selectedMessage={selectedMessage}
                  />
                </div>
                <div className="md:col-span-2">
                  {selectedMessage ? (
                    <MessageThread message={selectedMessage} />
                  ) : (
                    <Card>
                      <CardContent className="p-6 text-center text-muted-foreground">
                        Select a message to view
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="compose">
              <MessageComposer onMessageSent={handleMessageSent} />
            </TabsContent>

            <TabsContent value="sent">
              <MessageInbox
                messages={messages.filter(msg => 
                  msg.sender_id === currentUserId
                )}
                onMessageSelect={handleMessageRead}
                selectedMessage={selectedMessage}
                showSent={true}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};